import { useEffect, useLayoutEffect, useRef, useState, type MouseEvent } from 'react';
import { Link } from 'react-router-dom';
import { AGENT_LIST, AGENTS, isPubliclyLive, type AgentSlug } from '../agents/registry.ts';
import { withNext } from '../auth/postAuthRedirect.ts';
import AgentMeta from './AgentMeta.tsx';
import AgentNode from './AgentNode.tsx';
import GuestDock from './GuestDock.tsx';
import { GUEST_CHAT_ENABLED } from '../config/features.ts';
import './Hero.css';

/** Sentinel for "no agent has been hovered yet — show the brand default copy". */
type ActiveAgent = AgentSlug | 'default';

/** Engaging an agent now goes straight to sign-up (no interstitial agent page),
 *  carrying a `?next=` so the new account lands in that agent's console. */
function engagePath(slug: AgentSlug): string {
  return withNext('/auth/signup', `/agent/${slug}`);
}

/** Matches the mobile breakpoint in Hero.css; below this, the hero switches
 *  to a stacked layout and agent buttons no longer navigate on first tap. */
const MOBILE_QUERY = '(max-width: 720px)';

/** Fallback paths used during SSR / before first measurement. Match the
 *  legacy hard-coded values so a flash before the layout effect runs still
 *  looks approximately right. */
const INITIAL_CABLES: Record<AgentSlug, string> = {
  uno: 'M 25 40 Q 22 62 16 85',
  vera: 'M 25 40 Q 32 62 50 85',
  maximus: 'M 25 40 Q 50 64 84 85',
};

function useMobileLayout(): boolean {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(MOBILE_QUERY).matches,
  );
  useEffect(() => {
    const mq = window.matchMedia(MOBILE_QUERY);
    const handler = (event: MediaQueryListEvent) => setIsMobile(event.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return isMobile;
}

export default function Hero() {
  const [activeAgent, setActiveAgent] = useState<ActiveAgent>('default');
  const isMobile = useMobileLayout();
  const stageRef = useRef<HTMLDivElement>(null);
  const [cables, setCables] = useState<Record<AgentSlug, string>>(INITIAL_CABLES);

  /** Cable endpoints can't live as fixed viewBox y-values: the stage height
   *  grows as `100vh - 132px` while its width is capped at 1100px, so when
   *  the browser is zoomed out the stage becomes disproportionately tall
   *  and the octagon (sized off width) plus the agent rail (anchored at
   *  bottom: 4%) drift to different y%-of-stage than the hard-coded paths
   *  assumed. Measure real bounding boxes on resize and rebuild the paths
   *  in viewBox-percent space (preserveAspectRatio="none" maps viewBox
   *  units 1:1 to %-of-container, so the same numbers are what we need). */
  useLayoutEffect(() => {
    if (isMobile) {
      return;
    }
    const stage = stageRef.current;
    if (!stage) {
      return;
    }

    const recompute = () => {
      const sRect = stage.getBoundingClientRect();
      if (sRect.width === 0 || sRect.height === 0) {
        return;
      }
      const heroWrap = stage.querySelector<HTMLElement>('.om-hero__image-wrap');
      if (!heroWrap) {
        return;
      }
      const oRect = heroWrap.getBoundingClientRect();
      const sx = ((oRect.left + oRect.width / 2 - sRect.left) / sRect.width) * 100;
      const sy = ((oRect.bottom - sRect.top) / sRect.height) * 100;

      const pathFor = (slug: AgentSlug): string => {
        const portrait = stage.querySelector<HTMLElement>(
          `[data-agent="${slug}"] .om-agent-node__portrait`,
        );
        if (!portrait) {
          return INITIAL_CABLES[slug];
        }
        const pRect = portrait.getBoundingClientRect();
        const ex = ((pRect.left + pRect.width / 2 - sRect.left) / sRect.width) * 100;
        // Terminate at the portrait octagon's CENTER (top + half height), not
        // its top edge — the cable runs into the middle of each agent button.
        // The portrait sits above the cable layer, so the tail tucks under it.
        const ey = ((pRect.top + pRect.height / 2 - sRect.top) / sRect.height) * 100;
        // Quadratic curve with control point pulled slightly toward the
        // target's x and offset below the midpoint, giving each cable a
        // gentle droop so the three don't overlap as straight lines.
        const cx = sx + (ex - sx) * 0.55;
        const cy = (sy + ey) / 2 + 3;
        return `M ${sx.toFixed(2)} ${sy.toFixed(2)} Q ${cx.toFixed(2)} ${cy.toFixed(2)} ${ex.toFixed(2)} ${ey.toFixed(2)}`;
      };

      setCables({
        uno: pathFor('uno'),
        vera: pathFor('vera'),
        maximus: pathFor('maximus'),
      });
    };

    recompute();
    const ro = new ResizeObserver(recompute);
    ro.observe(stage);
    // Also observe individual anchors — fonts loading after first paint can
    // change the text height below each portrait, which shifts the portrait
    // up/down without the stage itself resizing.
    stage.querySelectorAll<HTMLElement>('[data-agent]').forEach((el) => ro.observe(el));
    const heroWrap = stage.querySelector<HTMLElement>('.om-hero__image-wrap');
    if (heroWrap) {
      ro.observe(heroWrap);
    }
    window.addEventListener('resize', recompute);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', recompute);
    };
  }, [isMobile]);

  /** On mobile, tapping an agent button previews the agent (sets activeAgent)
   *  instead of navigating — the user has to tap the ENGAGE link inside the
   *  preview to actually load the agent page. On desktop the click falls
   *  through to the underlying <Link>. */
  const handleAgentClick = (slug: AgentSlug) => (event: MouseEvent<HTMLAnchorElement>) => {
    if (isMobile) {
      event.preventDefault();
      setActiveAgent(slug);
    }
  };

  /** Shared inner contents of the hero octagon — extracted so the wrapper
   *  can switch between a non-interactive <div> (default state) and a
   *  <Link> (active agent state) without duplicating the layered images. */
  const octagonChildren = (
    <>
      <span className="om-hero__line om-hero__line--bot-left" />
      <span className="om-hero__line om-hero__line--top-right" />
      <div className="om-octagon">
        <div className="om-octagon__inner">
          <img
            className="om-hero__layer om-hero__layer--default"
            src={AGENTS.vera.image}
            alt=""
            loading="eager"
            decoding="async"
            draggable={false}
          />
          <img
            className="om-hero__layer om-hero__layer--uno"
            src={AGENTS.uno.image}
            alt=""
            loading="eager"
            decoding="async"
            draggable={false}
          />
          <img
            className="om-hero__layer om-hero__layer--vera"
            src={AGENTS.vera.image}
            alt=""
            loading="eager"
            decoding="async"
            draggable={false}
          />
          <img
            className="om-hero__layer om-hero__layer--maximus"
            src={AGENTS.maximus.image}
            alt=""
            loading="eager"
            decoding="async"
            draggable={false}
          />
          <div className="om-octagon__vignette" />
          <div className="om-octagon__scanlines" />
        </div>
      </div>
    </>
  );

  return (
    <>
      <section className={GUEST_CHAT_ENABLED ? 'om-hero om-hero--guest' : 'om-hero'}>
        <div className="om-hero-stage" ref={stageRef} data-active-agent={activeAgent}>
          {/* HUD cables: paths are measured on resize from the hero octagon's
            bottom-center to each agent portrait's center. The viewBox
            is 0–100 with preserveAspectRatio="none" so coordinates map 1:1
            to percent-of-stage in each axis. */}
          <svg
            className="om-hero-stage__cables"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <path className="om-hero-cable om-hero-cable--uno" d={cables.uno} />
            <path className="om-hero-cable om-hero-cable--vera" d={cables.vera} />
            <path className="om-hero-cable om-hero-cable--maximus" d={cables.maximus} />
          </svg>

          {/* Hero octagon (left column). SYSTEMS · ONLINE eyebrow sits ABOVE
            the octagon to keep the cable fan zone clear. When an agent is
            active, the octagon itself is a Link to that agent's page —
            same destination as the ENGAGE button below. */}
          <div className="om-hero__col-image">
            <div className="om-hero__eyebrow">
              <span className="om-hero__dot" /> SYSTEMS · ONLINE
            </div>
            {activeAgent === 'default' ? (
              <div className="om-hero__image-wrap" aria-hidden="true">
                {octagonChildren}
              </div>
            ) : (
              <Link
                to={engagePath(activeAgent)}
                className="om-hero__image-wrap om-hero__image-wrap--active"
                aria-label={`Engage agent ${AGENTS[activeAgent].name}`}
              >
                {octagonChildren}
              </Link>
            )}
          </div>

          {/* Hero content (right column). Title row uses min-height + flex-end
            so the title baseline rests on the octagon's HUD top-right line. */}
          <div className="om-hero__content">
            <div className="om-hero__title-row">
              <h1 className="om-hero__title">
                Oculus<span className="om-hero__title-accent">Mind</span>.AI
              </h1>
            </div>

            <div className="om-hero__meta-stack">
              {activeAgent === 'default' ? (
                <div className="om-hero__meta-default" key="default">
                  <p className="om-hero__tagline">
                    <span>Agents that search.</span> <span>Agents that think.</span>{' '}
                    <span>Agents that act.</span>
                  </p>
                  <p className="om-hero__sub">Autonomous Agents currently in stealth mode</p>
                </div>
              ) : (
                <AgentMeta key={activeAgent} agent={AGENTS[activeAgent]} />
              )}
            </div>

            {/* Action / hint slot — mutually exclusive. In default state the
              hint shows here (and on mobile it sits directly above the
              agent rail thanks to flex order). When an agent is active,
              the ENGAGE button replaces the hint and on mobile is lifted
              above the title to land right under the octagon. */}
            {activeAgent === 'default' ? (
              <p className="om-hero__hint">
                <code>$ select</code>{' '}
                <span className="om-hero__cta-status om-hero__cta-status--hover">
                  → HOVER AN AGENT
                </span>
                <span className="om-hero__cta-status om-hero__cta-status--tap">→ TAP AN AGENT</span>
              </p>
            ) : (
              <div className="om-hero__action">
                <Link
                  key={activeAgent}
                  to={engagePath(activeAgent)}
                  className="om-hero__engage"
                  aria-label={`Engage agent ${AGENTS[activeAgent].name}`}
                >
                  <code>$ engage</code>
                  <span className="om-hero__engage-arrow" aria-hidden="true">
                    →
                  </span>
                  <span className="om-hero__engage-name">
                    ACTIVATE {AGENTS[activeAgent].name.toUpperCase()}
                  </span>
                </Link>
              </div>
            )}

            {/* Guest dock (tiers T7, Beta): try Uno without an account.
                Focusing the field selects Uno in the hero — typing IS
                engaging the perception core. */}
            {GUEST_CHAT_ENABLED ? (
              <GuestDock
                // Focusing/clicking the field selects Uno everywhere (user
                // directive 2026-07-06) — guests chat with Uno, so engaging
                // the dock IS engaging the perception core.
                onEngage={() => setActiveAgent('uno')}
                autoFocus={!isMobile}
                isMobile={isMobile}
              />
            ) : null}
          </div>

          {/* Agent rail at the bottom of the stage. */}
          {AGENT_LIST.map((agent) => (
            <AgentNode
              key={agent.slug}
              agent={agent}
              onMouseEnter={() => setActiveAgent(agent.slug)}
              onClick={handleAgentClick(agent.slug)}
            />
          ))}
        </div>
      </section>

      {/* "What <agent> can do" — a full-width section BELOW the hero fold,
          populated by the hovered agent. The jump link under the hero blurb
          (in AgentMeta) scrolls here. Only AVAILABLE agents with a
          capabilities list contribute a section — an in-training agent never
          advertises capabilities it can't yet deliver (Vera carries caps for
          the beta build, where she IS available, but stays quiet here in
          local/prod builds). */}
      {activeAgent !== 'default' && AGENTS[activeAgent].capabilities?.length ? (
        <section className="om-hero-caps" id="agent-caps">
          <div className="om-hero-caps__inner">
            {/* Centered engage CTA above the capabilities — same destination as
                the hero engage button (sign-up / Create account, carrying the
                ?next= into this agent's console). */}
            <div className="om-hero-caps__cta">
              <Link
                to={engagePath(activeAgent)}
                className="om-hero__engage"
                aria-label={`Activate agent ${AGENTS[activeAgent].name}`}
              >
                <code>$ engage</code>
                <span className="om-hero__engage-arrow" aria-hidden="true">
                  →
                </span>
                <span className="om-hero__engage-name">
                  ACTIVATE {AGENTS[activeAgent].name.toUpperCase()}
                </span>
              </Link>
            </div>
            <h2 className="om-hero-caps__title">
              {/* PUBLIC tense: GA agents read "can do"; an admin-gated preview
                  (Maximus) reads "will do / IN TRAINING" to the public even in
                  beta builds where it is `available` for the admin chooser. */}
              What {AGENTS[activeAgent].name}{' '}
              {isPubliclyLive(AGENTS[activeAgent]) ? 'can' : 'will'} do
              {isPubliclyLive(AGENTS[activeAgent]) ? null : (
                <span className="om-hero-caps__training">IN TRAINING</span>
              )}
            </h2>
            <ul className="om-hero-caps__list">
              {AGENTS[activeAgent].capabilities.map((cap) => (
                <li key={cap.tag}>
                  <span className="om-hero-caps__tag">{cap.tag}</span>
                  {cap.text}
                </li>
              ))}
            </ul>
          </div>
        </section>
      ) : null}
    </>
  );
}
